/**
 * Leaderboards - Campus rankings and competition
 * Premium dark mode design with glass-morphism effects
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useShareMilestone } from '@/components/sharing';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Trophy,
  Medal,
  Users,
  Building2,
  GraduationCap,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  Crown,
  Flame,
  Star,
  ChevronRight,
  Loader2,
  Sparkles,
  PartyPopper,
  MapPin,
  Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Glass panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={cn(
      'bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// Period options with semester support
const periods = [
  { value: 'all_time', label: 'All Time' },
  { value: 'semester', label: 'Semester' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
];

// Rank badge colors and icons
const getRankStyle = (rank) => {
  if (rank === 1) return { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', icon: Crown, glow: 'shadow-amber-500/20' };
  if (rank === 2) return { bg: 'bg-slate-300/10', border: 'border-slate-300/30', text: 'text-slate-300', icon: Medal, glow: '' };
  if (rank === 3) return { bg: 'bg-orange-700/20', border: 'border-orange-700/50', text: 'text-orange-500', icon: Medal, glow: '' };
  return { bg: 'bg-white/[0.03]', border: 'border-white/[0.06]', text: 'text-white/60', icon: null, glow: '' };
};

// ==========================================
// PREMIUM RANKING CARD (Top 3)
// ==========================================
const PremiumRankCard = ({ entry, rank, type = 'student', isCurrentUser = false }) => {
  const style = getRankStyle(rank);
  const RankIcon = style.icon;
  
  const getTypeIcon = () => {
    switch (type) {
      case 'organization': return Building2;
      case 'event': return PartyPopper;
      case 'campus': return GraduationCap;
      default: return Users;
    }
  };
  
  const TypeIcon = getTypeIcon();
  
  return (
    <div
      className={cn(
        'relative p-6 rounded-2xl border transition-all duration-300',
        style.bg, style.border,
        rank === 1 ? 'shadow-lg shadow-amber-500/10' : '',
        isCurrentUser ? 'ring-2 ring-cyan-400/50' : '',
        'hover:scale-[1.02] hover:shadow-xl'
      )}
      data-testid={`premium-rank-card-${rank}`}
    >
      {/* Rank Badge */}
      <div className={cn(
        'absolute -top-3 -right-3 w-12 h-12 rounded-xl flex items-center justify-center',
        style.bg, style.border, 'border'
      )}>
        {RankIcon ? (
          <RankIcon className={cn('w-6 h-6', style.text)} />
        ) : (
          <span className={cn('text-lg font-bold font-mono', style.text)}>{rank}</span>
        )}
      </div>
      
      {/* Flame effect for #1 */}
      {rank === 1 && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-amber-500/5 to-transparent pointer-events-none" />
      )}
      
      {/* Content */}
      <div className="flex items-center gap-4">
        {/* Avatar/Icon */}
        <div className={cn(
          'w-16 h-16 rounded-xl flex items-center justify-center',
          rank === 1 ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' : 'bg-white/[0.05]'
        )}>
          {entry.avatar_url ? (
            <img src={entry.avatar_url} alt="" className="w-full h-full rounded-xl object-cover" />
          ) : (
            <TypeIcon className={cn('w-8 h-8', style.text)} />
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={cn(
              'font-semibold truncate',
              isCurrentUser ? 'text-cyan-400' : 'text-white'
            )}>
              {entry.display_name || 'Anonymous'}
            </h3>
            {isCurrentUser && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">You</span>
            )}
          </div>
          <p className="text-sm text-white/50 mt-0.5">
            {entry.metric_label || 'Score'}: <span className="text-white/70 font-mono">{Math.round(entry.score).toLocaleString()}</span>
          </p>
          {type === 'event' && entry.metadata?.organization_name && (
            <p className="text-xs text-white/40 mt-1">by {entry.metadata.organization_name}</p>
          )}
        </div>
      </div>
      
      {/* Score */}
      <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className={cn('w-4 h-4', style.text)} />
          <span className={cn('text-2xl font-bold font-mono', style.text)}>
            {Math.round(entry.score).toLocaleString()}
          </span>
        </div>
        {rank === 1 && <Flame className="w-5 h-5 text-amber-400 animate-pulse" />}
      </div>
    </div>
  );
};

// ==========================================
// COMPACT RANKING TABLE
// ==========================================
const CompactRankingTable = ({ entries, type = 'student', currentUserId, startRank = 4 }) => {
  if (!entries || entries.length === 0) return null;
  
  return (
    <div className="rounded-xl border border-white/[0.06] overflow-hidden" data-testid="compact-ranking-table">
      <table className="w-full">
        <thead>
          <tr className="bg-white/[0.02] border-b border-white/[0.06]">
            <th className="px-4 py-3 text-left text-xs font-medium text-white/40 uppercase tracking-wider w-16">Rank</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-white/40 uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-white/40 uppercase tracking-wider">Score</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {entries.map((entry, idx) => {
            const rank = entry.rank_position || (startRank + idx);
            const isCurrentUser = entry.user_id === currentUserId;
            
            return (
              <tr 
                key={entry.id || idx}
                className={cn(
                  'transition-colors',
                  isCurrentUser ? 'bg-cyan-500/10' : 'hover:bg-white/[0.02]'
                )}
              >
                <td className="px-4 py-3">
                  <span className={cn(
                    'font-mono font-medium',
                    rank <= 10 ? 'text-violet-400' : 'text-white/50'
                  )}>
                    #{rank}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-white/60">
                        {(entry.display_name || 'A')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className={cn(
                        'font-medium truncate',
                        isCurrentUser ? 'text-cyan-400' : 'text-white'
                      )}>
                        {entry.display_name || 'Anonymous'}
                        {isCurrentUser && <span className="ml-2 text-xs text-cyan-400">(You)</span>}
                      </p>
                      {type === 'event' && entry.metadata?.organization_name && (
                        <p className="text-xs text-white/40 truncate">{entry.metadata.organization_name}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono font-semibold text-white">
                    {Math.round(entry.score).toLocaleString()}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ==========================================
// YOUR RANK CARD
// ==========================================
const YourRankCard = ({ rank, total, period, onShare }) => {
  if (!rank) {
    return (
      <GlassPanel className="p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-white/[0.05] flex items-center justify-center">
            <Trophy className="w-7 h-7 text-white/30" />
          </div>
          <div>
            <h3 className="text-white font-medium">Not Yet Ranked</h3>
            <p className="text-sm text-white/50 mt-0.5">
              Attend events to appear on the leaderboard!
            </p>
          </div>
        </div>
      </GlassPanel>
    );
  }

  const percentile = total > 0 ? Math.round((1 - (rank.rank_position - 1) / total) * 100) : 0;
  
  return (
    <GlassPanel className="p-6 bg-gradient-to-br from-cyan-500/10 via-transparent to-violet-500/5" data-testid="your-rank-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
            <span className="text-2xl font-mono font-bold text-cyan-400">
              #{rank.rank_position}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Your Rank</h3>
            <p className="text-white/60">
              Top <span className="text-cyan-400 font-semibold">{percentile}%</span> of students
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-mono">
                {Math.round(rank.score).toLocaleString()} points
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <div className="hidden md:block text-right">
            <p className="text-xs text-white/40 uppercase tracking-wider">{period.replace('_', ' ')}</p>
            <p className="text-white/60 mt-1">
              {total.toLocaleString()} total students
            </p>
          </div>
          {onShare && (
            <Button
              onClick={onShare}
              size="sm"
              className="bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 border border-violet-500/30"
              data-testid="share-rank-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </Button>
          )}
        </div>
      </div>
    </GlassPanel>
  );
};

// ==========================================
// EMPTY STATE
// ==========================================
const EmptyState = ({ icon: Icon, title, description }) => (
  <GlassPanel className="p-12 text-center">
    <Icon className="w-14 h-14 text-white/20 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-white mb-2">{title}</h3>
    <p className="text-white/50 max-w-md mx-auto">{description}</p>
  </GlassPanel>
);

// ==========================================
// LEADERBOARD TAB CONTENT
// ==========================================
const LeaderboardTab = ({ entries, type, currentUserId, isLoading, emptyIcon, emptyTitle, emptyDesc }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }
  
  if (!entries || entries.length === 0) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDesc} />;
  }
  
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);
  
  return (
    <div className="space-y-6">
      {/* Top 3 Premium Cards */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {top3.map((entry, idx) => (
            <PremiumRankCard
              key={entry.id || idx}
              entry={entry}
              rank={entry.rank_position || idx + 1}
              type={type}
              isCurrentUser={entry.user_id === currentUserId}
            />
          ))}
        </div>
      )}
      
      {/* Compact Table for rest */}
      {rest.length > 0 && (
        <CompactRankingTable
          entries={rest}
          type={type}
          currentUserId={currentUserId}
          startRank={4}
        />
      )}
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export const Leaderboards = () => {
  const { user, session } = useAuth();
  const { shareRank } = useShareMilestone();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [period, setPeriod] = useState('all_time');
  const [studentLeaderboard, setStudentLeaderboard] = useState([]);
  const [orgLeaderboard, setOrgLeaderboard] = useState([]);
  const [eventLeaderboard, setEventLeaderboard] = useState([]);
  const [campusRankings, setCampusRankings] = useState([]);
  const [myRank, setMyRank] = useState(null);
  
  // Handle share rank
  const handleShareRank = useCallback(() => {
    if (!myRank) return;
    shareRank({
      rank: myRank.rank_position,
      total: studentLeaderboard.length || 100,
      score: myRank.score,
      type: 'student',
      period: period === 'all_time' ? 'All Time' : period.charAt(0).toUpperCase() + period.slice(1),
    });
  }, [myRank, studentLeaderboard.length, period, shareRank]);

  // Fetch leaderboard data
  const fetchLeaderboards = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${session.access_token}` };

      const [studentsRes, orgsRes, eventsRes, campusRes, myRankRes] = await Promise.all([
        fetch(`${API_URL}/api/leaderboards/students?period=${period}&limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboards/organizations?period=${period}&limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboards/events?period=${period}&limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboards/campuses?period=${period}&limit=50`, { headers }),
        fetch(`${API_URL}/api/leaderboards/students/my-rank?period=${period}`, { headers }),
      ]);

      if (studentsRes.ok) {
        const data = await studentsRes.json();
        setStudentLeaderboard(data.leaderboard || []);
      }

      if (orgsRes.ok) {
        const data = await orgsRes.json();
        setOrgLeaderboard(data.leaderboard || []);
      }
      
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEventLeaderboard(data.leaderboard || []);
      }

      if (campusRes.ok) {
        const data = await campusRes.json();
        setCampusRankings(data.rankings || []);
      }

      if (myRankRes.ok) {
        const data = await myRankRes.json();
        setMyRank(data.rank);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboards:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, period]);

  useEffect(() => {
    fetchLeaderboards();
  }, [fetchLeaderboards]);

  if (loading && !studentLeaderboard.length) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-white/50 text-sm">Loading leaderboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="leaderboards-page" className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Trophy className="w-8 h-8 text-amber-400" />
            Leaderboards
          </h1>
          <p className="text-white/50 mt-1">See who's making waves on campus</p>
        </div>

        {/* Period Selector */}
        <div className="flex items-center gap-1 bg-white/[0.03] rounded-xl p-1 border border-white/[0.06]">
          {periods.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                period === p.value 
                  ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' 
                  : 'text-white/50 hover:text-white hover:bg-white/[0.05]'
              )}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Your Rank */}
      <YourRankCard 
        rank={myRank} 
        total={studentLeaderboard.length || 100}
        period={period}
        onShare={myRank ? handleShareRank : null}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white/[0.03] border border-white/[0.06] p-1.5 rounded-xl w-full grid grid-cols-4 gap-1">
          <TabsTrigger 
            value="students" 
            className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg py-2.5"
            data-testid="tab-students"
          >
            <Users className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Students</span>
          </TabsTrigger>
          <TabsTrigger 
            value="organizations"
            className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg py-2.5"
            data-testid="tab-organizations"
          >
            <Building2 className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Orgs</span>
          </TabsTrigger>
          <TabsTrigger 
            value="events"
            className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg py-2.5"
            data-testid="tab-events"
          >
            <PartyPopper className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Events</span>
          </TabsTrigger>
          <TabsTrigger 
            value="campuses"
            className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg py-2.5"
            data-testid="tab-campuses"
          >
            <GraduationCap className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Campuses</span>
          </TabsTrigger>
        </TabsList>

        {/* Students Tab */}
        <TabsContent value="students" className="mt-6">
          <LeaderboardTab
            entries={studentLeaderboard}
            type="student"
            currentUserId={user?.id}
            isLoading={loading}
            emptyIcon={Trophy}
            emptyTitle="No Rankings Yet"
            emptyDesc="Check back after more students attend events!"
          />
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="mt-6">
          <LeaderboardTab
            entries={orgLeaderboard}
            type="organization"
            currentUserId={null}
            isLoading={loading}
            emptyIcon={Building2}
            emptyTitle="No Organization Rankings"
            emptyDesc="Organizations will be ranked based on event engagement."
          />
        </TabsContent>
        
        {/* Events Tab */}
        <TabsContent value="events" className="mt-6">
          <LeaderboardTab
            entries={eventLeaderboard}
            type="event"
            currentUserId={null}
            isLoading={loading}
            emptyIcon={PartyPopper}
            emptyTitle="No Event Rankings"
            emptyDesc="Events will be ranked by check-in attendance."
          />
        </TabsContent>

        {/* Campuses Tab */}
        <TabsContent value="campuses" className="mt-6">
          {campusRankings.length > 0 ? (
            <div className="space-y-6">
              {/* Top 3 Campus Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {campusRankings.slice(0, 3).map((entry, idx) => {
                  const rank = entry.rank_position || idx + 1;
                  const style = getRankStyle(rank);
                  const campus = entry.campuses || {};
                  
                  return (
                    <div
                      key={entry.id || idx}
                      className={cn(
                        'relative p-6 rounded-2xl border transition-all',
                        style.bg, style.border,
                        'hover:scale-[1.02]'
                      )}
                    >
                      {/* Rank Badge */}
                      <div className={cn(
                        'absolute -top-3 -right-3 w-10 h-10 rounded-lg flex items-center justify-center font-mono font-bold',
                        style.bg, style.border, 'border', style.text
                      )}>
                        {rank}
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center">
                          <GraduationCap className={cn('w-7 h-7', style.text)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">
                            {campus.name || 'Unknown Campus'}
                          </h3>
                          {(campus.city || campus.state) && (
                            <p className="text-sm text-white/50 flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3" />
                              {[campus.city, campus.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold font-mono text-white">{entry.total_active_organizations || 0}</p>
                          <p className="text-xs text-white/40">Organizations</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold font-mono text-white">{entry.total_checkins || 0}</p>
                          <p className="text-xs text-white/40">Check-ins</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Rest of campuses in table */}
              {campusRankings.length > 3 && (
                <div className="rounded-xl border border-white/[0.06] overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-white/[0.02] border-b border-white/[0.06]">
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/40 uppercase w-16">Rank</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/40 uppercase">Campus</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/40 uppercase">Orgs</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/40 uppercase">Check-ins</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {campusRankings.slice(3).map((entry, idx) => {
                        const campus = entry.campuses || {};
                        return (
                          <tr key={entry.id || idx} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3">
                              <span className="font-mono text-white/50">#{entry.rank_position || idx + 4}</span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-white">{campus.name || 'Unknown'}</p>
                              {(campus.city || campus.state) && (
                                <p className="text-xs text-white/40">{[campus.city, campus.state].filter(Boolean).join(', ')}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-white">{entry.total_active_organizations || 0}</td>
                            <td className="px-4 py-3 text-right font-mono text-white">{entry.total_checkins || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <EmptyState
              icon={GraduationCap}
              title="Campus Rankings Coming Soon"
              description="Campuses will be ranked based on overall activity."
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Leaderboards;
